/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createPath } from 'history';
import _ from 'lodash';
import urlJoin from 'proper-url-join';
import { generatePath, matchPath } from 'react-router-dom';

import history from '@ibm-watson/shared/modules/ia/util/history';

const BASE_PARAMS = ['instance'];

const HASH_PARAMS = ['#'];

/**
 * PATHS is a map of legacy state names to path definition objects.
 * The href function uses this to determine the path structure
 * and parameter names required to build the URL.
 */
const PATHS = {
  instance: {
    path: '/:instance',
    requiredParams: BASE_PARAMS
  },
  assistants: {
    path: '/:instance/assistants',
    requiredParams: BASE_PARAMS,
    parent: 'instance'
  },
  authoring: {
    path: '/:instance/skills/:skillId/authoring',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skill'
  },
  assistant: {
    path: '/:instance/assistants/:assistantId',
    requiredParams: BASE_PARAMS.concat(['assistantId']),
    parent: 'assistants'
  },
  skills: {
    path: '/:instance/skills',
    requiredParams: BASE_PARAMS,
    parent: 'instance'
  },
  skill: {
    path: '/:instance/skills/:skillId',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skills'
  },
  catalogx: {
    path: '/:instance/skills/:skillId/build/catalog',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skill'
  },
  versions: {
    path: '/:instance/skills/:skillId/build/versions',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skill'
  },
  improve: {
    path: '/:instance/skills/:skillId/build/improve',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skill'
  },
  overview: {
    path: '/:instance/skills/:skillId/build/improve/overview',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'improve'
  },
  autolearn: {
    path: '/:instance/skills/:skillId/build/improve/autolearn',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'improve'
  },
  userdata: {
    path: '/:instance/skills/:skillId/build/improve/userdata',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'improve'
  },
  intents: {
    path: '/:instance/skills/:skillId/build/intents',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skill'
  },
  entities: {
    path: '/:instance/skills/:skillId/build/entities',
    requiredParams: BASE_PARAMS.concat(['skillId']),
    parent: 'skill'
  }
};

/**
 * Extract any parameters for the named state that exist in the
 * current location. Uses the PATHS definitions in this module.
 * If the definition for the current state does not match, it will try
 * and match the states 'parent' recursively until no 'parent' is defined.
 * @param {String} stateName the name of the state
 * @return {Object} A map of parameter names to values
 */
const getParamsFromLocation = stateName => {
  // Pick the path out
  const pathObj = PATHS[stateName];

  // extract any parameters that we can from the current path
  const match = matchPath(pathObj.path, history.location.pathname);

  if (match?.params) {
    return Object.keys(match.params).reduce((accumulator, key) => {
      accumulator[key] = decodeURIComponent(match.params[key]);
      return accumulator;
    }, {});
  }

  // if there was no match, try with the parent
  if (pathObj.parent) {
    return getParamsFromLocation(pathObj.parent);
  }

  return {};
};

/**
 * A substitute for $state.href from angular-ui-router
 *
 * @param {String} stateName The name of the state to get the ref for
 * @param {Object} props The list of props to use to calculate the href. Contains a mix of path and string params
 * @returns {String} the reference for the state
 */
export const href = (stateName, props: { instance?: string } = {}) => {
  // Pick the path out
  const pathObj = PATHS[stateName];

  if (!pathObj) {
    throw new Error(
      `Could not calculate reference: No state exists with name '${stateName}'.`
    );
  }
  // some components pass raw instance id e.g. ..us-south:a/d63.. -- need to replace / with %2F
  if (props.instance && props.instance.indexOf('/') !== -1) {
    props.instance = encodeURIComponent(props.instance);
  }

  const locationProps = getParamsFromLocation(stateName);

  const mergedProps = Object.assign({}, locationProps, props);
  const mergedKeys = Object.keys(mergedProps);

  // Make sure all the required parameters exist
  const missingParameters = _.difference(pathObj.requiredParams, mergedKeys);

  if (missingParameters.length > 0) {
    throw new Error(
      `Could not calculate state reference: Missing parameters '${missingParameters.join(
        ','
      )}'.`
    );
  }

  // parameter named hash will go in the hash
  const hashParams = mergedProps['#'];
  // additional props not required by the path or hash go to a query string
  const queryKeys = _.difference(
    mergedKeys,
    pathObj.requiredParams,
    HASH_PARAMS
  );
  const queryParams = _.pick(mergedProps, queryKeys);
  const search = generateParameterizedUrl(queryParams);
  const genPath = generatePath(pathObj.path, mergedProps);

  return createPath({
    pathname: urlJoin(document.baseURI, genPath),
    search,
    hash: hashParams
  });
};

const generateParameterizedUrl = params => {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : '';
};

/**
 * A substitute for $state.go from angular-ui-router.
 * Go to the page represented by the legacy state name.
 * Uses the history push API under the covers.
 * @param {String} stateName The name of the state to go to
 * @param {Object} props an object representing a map of prop names to values
 * @returns {undefined}
 */
export const go = (stateName, props) => {
  const newHref = href(stateName, props);
  history.push(newHref);
};

export const transitionTo = (stateName, props = {}) => {
  /* eslint-disable no-console */
  console.log('state.transitionTo', stateName, props);
  /* eslint-enable no-console */
};

export const reload = () => {
  /* eslint-disable no-console */
  console.log('state.reload');
  /* eslint-enable no-console */
};
